import React, { useState, useRef, useEffect } from 'react';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';

const ShareOptions = () => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showOrderOptions, setShowOrderOptions] = useState(false);
  // Removed showMailTip as it's less necessary with direct Gmail link

  const shareRef = useRef(null);
  const orderRef = useRef(null);

  const toggleShareOptions = () => {
    setShowShareOptions((prev) => !prev);
    // If share is opening, close order options
    if (!showShareOptions) {
      setShowOrderOptions(false);
    }
  };

  const toggleOrderOptions = () => {
    setShowOrderOptions((prev) => !prev);
    // If order is opening, close share options
    if (!showOrderOptions) {
      setShowShareOptions(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Function to open Gmail compose with pre-filled data
  const openGmailCompose = (to, subject, body) => {
    // Construct the Gmail URL
    // mail.google.com/mail/u/0/ refers to the first Gmail account logged in.
    // ?view=cm ensures it's a compose window
    // &fs=1 makes it fullscreen (optional, might be ignored by some browsers/settings)
    // &tf=cm also works for compose (sometimes used interchangeably with view=cm)
    // &to= recipient email
    // &su= subject (su for subject)
    // &body= email body
    const gmailUrl = `https://mail.google.com/mail/u/0/?view=cm&fs=1` +
                     `${to ? `&to=${encodeURIComponent(to)}` : ''}` +
                     `${subject ? `&su=${encodeURIComponent(subject)}` : ''}` +
                     `${body ? `&body=${encodeURIComponent(body)}` : ''}`;
    
    window.open(gmailUrl, "_blank");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdowns if click is outside both share and order refs
      if (
        shareRef.current && !shareRef.current.contains(event.target) &&
        orderRef.current && !orderRef.current.contains(event.target)
      ) {
        setShowShareOptions(false);
        setShowOrderOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Adjusted button classes for slightly larger buttons and no hover on touch devices
  const buttonClasses =
    "flex items-center justify-center px-4 py-2 text-sm min-w-[130px] h-10 " + // Mobile (base)
    "sm:px-7 sm:py-2.5 sm:text-base sm:min-w-[160px] sm:h-12 " + // Tablet and above
    "text-white rounded-xl transition-all duration-300 shadow-md transform font-medium " +
    "@media (hover: hover) {&:hover { transform: scale(1.05) translateY(-4px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); } } " + // Hover for non-touch
    "group hover:ring-2 hover:ring-gray-700 hover:ring-opacity-50"; // Ring for hover

  // Dropdown positioning classes
  const dropdownClasses =
    "absolute mt-3 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 transform origin-top-right transition-all duration-200 " +
    "left-1/2 -translate-x-1/2 " + // Center horizontally for mobile
    "sm:left-auto sm:right-0 sm:translate-x-0"; // Align to right for larger screens


  return (
    <div className="grid grid-cols-2 gap-4 sm:flex sm:items-center sm:gap-8">
      {/* Order Now Button */}
      <div className="relative" ref={orderRef}>
        <button
          onClick={toggleOrderOptions}
          className={`${buttonClasses} bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700`}
        >
          <SendIcon
            className="mr-2 sm:mr-3 transform group-hover:translate-x-2 transition-transform duration-300"
            style={{ fontSize: '22px' }}
          />
          <span className="group-hover:tracking-wide transition-all duration-300">Order Now</span>
        </button>

        {/* Order Dropdown */}
        {showOrderOptions && (
          <div className={dropdownClasses}>
            <div className="p-2 space-y-1">
              <button
                onClick={() => {
                  const phoneNumber = "919016663115";
                  const message = encodeURIComponent(`Hello, I would like to place an order for this artwork.\n\n${window.location.href}`);
                  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
                  window.open(whatsappUrl, "_blank");
                }}
                className="flex items-center gap-4 w-full p-4 rounded-lg hover:bg-green-50 transition-all duration-200 group hover:scale-[1.02]"
              >
                <WhatsAppIcon className="text-green-500 group-hover:text-green-600 transform group-hover:scale-110 transition-transform duration-200" style={{ fontSize: "24px" }} />
                <span className="text-gray-700 font-medium group-hover:tracking-wide transition-all duration-200">WhatsApp</span>
              </button>
              <button
                onClick={() => openGmailCompose(
                  "info.vizaura@gmail.com",
                  "Quotation Request",
                  `Hello, I want a quotation to order\n\n${window.location.href}`
                )}
                className="flex items-center gap-4 w-full p-4 rounded-lg hover:bg-blue-50 transition-all duration-200 group hover:scale-[1.02]"
              >
                <MailOutlineIcon className="text-blue-500 group-hover:text-blue-600 transform group-hover:scale-110 transition-transform duration-200" style={{ fontSize: "24px" }} />
                <span className="text-gray-700 font-medium group-hover:tracking-wide transition-all duration-200">Email (Gmail)</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Share Button */}
      <div className="relative" ref={shareRef}>
        <button
          onClick={toggleShareOptions}
          className={`${buttonClasses} bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700`}
        >
          <ShareIcon
            className="mr-2 sm:mr-3 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300"
            style={{ fontSize: '22px' }}
          />
          <span className="group-hover:tracking-wide transition-all duration-300">Share</span>
        </button>

        {/* Share Options Dropdown */}
        {showShareOptions && (
          <div className={dropdownClasses}>
            <div className="p-2 space-y-1">
              <button
                onClick={copyLink}
                className="flex items-center gap-4 w-full p-4 rounded-lg hover:bg-gray-50 transition-all duration-200 group hover:scale-[1.02]"
              >
                <ContentCopyIcon
                  className="text-gray-500 group-hover:text-gray-700 transform group-hover:scale-110 transition-transform duration-200"
                  style={{ fontSize: '24px' }}
                />
                <span className="text-gray-700 font-medium group-hover:tracking-wide transition-all duration-200">Copy Link</span>
              </button>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent("Hey, check this out: " + window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 w-full p-4 rounded-lg hover:bg-green-50 transition-all duration-200 group hover:scale-[1.02]"
              >
                <WhatsAppIcon className="text-green-500 group-hover:text-green-600 transform group-hover:scale-110 transition-transform duration-200" style={{ fontSize: '24px' }} />
                <span className="text-gray-700 font-medium group-hover:tracking-wide transition-all duration-300">WhatsApp</span>
              </a>
              <button
                onClick={() => openGmailCompose(
                  "", // No specific 'to' for sharing, user can add
                  "Check this out",
                  `Here is something interesting: ${window.location.href}`
                )}
                className="flex items-center gap-4 w-full p-4 rounded-lg hover:bg-blue-50 transition-all duration-200 group hover:scale-[1.02]"
              >
                <MailOutlineIcon className="text-blue-500 group-hover:text-blue-600 transform group-hover:scale-110 transition-transform duration-200" style={{ fontSize: '24px' }} />
                <span className="text-gray-700 font-medium group-hover:tracking-wide transition-all duration-300">Email (Gmail)</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-800 px-6 py-3 rounded-xl shadow-lg border border-green-200 flex items-center gap-3 z-50">
          <ContentCopyIcon style={{ fontSize: '20px' }} />
          <span className="font-medium">Link copied successfully!</span>
        </div>
      )}
      {/* The mail tip message is removed as the direct Gmail link should open a visible tab. */}
    </div>
  );
};

export default ShareOptions;